<script setup lang="ts">
import type { Schemas } from '#shopware'
import { useWindowSize } from '@vueuse/core'

const props = defineProps<{
    slide: Schemas['HeroSlide']
}>()

const { width: windowWidth } = useWindowSize()

const isMobile = computed(() => windowWidth.value <= 768)
const isTablet = computed(() => windowWidth.value > 768 && windowWidth.value <= 1024)
const isDesktop = computed(() => windowWidth.value > 1024)

const countOfReducedText1InBanderole = computed(() => {
    if (!hasContent(props.slide.banderoleText2)) {
        if (isMobile.value) return 1
        if (isTablet.value) return 1
        return 3
    } else {
        if (isMobile.value) return 1
        if (isTablet.value) return 1
        return 2
    }
})

const countOfReducedText2InBanderole = computed(() => {
    if (!hasContent(props.slide.banderoleText2)) return 0
    if (isMobile.value || isTablet.value) return 0
    return 1
})

const countOfText1InBanderole = computed(() => {
    if (props.slide.isReducedMode) return 0
    if (!hasContent(props.slide.banderoleText2)) {
        if (isMobile.value) return 1
        if (windowWidth.value > 768 && windowWidth.value < 1440) return 2
        return 5
    } else {
        if (isMobile.value) return 1
        if (windowWidth.value > 768 && windowWidth.value < 1440) return 3
        return 3
    }
})

const countOfText2InBanderole = computed(() => {
    if (props.slide.isReducedMode) return 0
    if (hasContent(props.slide.banderoleText2)) {
        if (isMobile.value) return 1
        if (windowWidth.value > 768 && windowWidth.value < 1440) return 1
        return 2
    }
    return 0
})

const totalTextCounts = computed(() => {
    if (props.slide.isReducedMode) {
        return countOfReducedText1InBanderole.value + countOfReducedText2InBanderole.value
    }
    return countOfText1InBanderole.value + countOfText2InBanderole.value
})

function hasContent(input: string | null | undefined): input is string {
    return input != null && input.trim() !== ''
}
</script>

<template>
    <div v-if="slide.countdownEnd">
        <div
            class="banderole-timer absolute left-0 right-0 top-0 flex px-12 py-3 text-sm md:text-lg text-base-100 font-bold"
            :class="{'bg-base-content': !slide.banderoleBgColor, 'uppercase': slide.banderoleTextAlLCaps}"
            :style="slide.banderoleBgColor ? {'background-color': slide.banderoleBgColor} : undefined"
        >
            <div
                v-if="slide.isReducedMode"
                class="flex justify-center lg:justify-between items-center text-lg w-full"
                :class="slide.countdownType === 'A' ? 'h-12' : 'h-11'"
            >
                <div v-if="isDesktop">{{ slide.banderoleText1 }}</div>

                <div
                    class="flex justify-center items-center"
                    :class="slide.countdownType === 'A' ? 'h-12' : 'h-11'"
                >
                    <div v-if="hasContent(slide.countdownText)" class="pr-6 text-sm md:text-lg">
                        {{ slide.countdownText }}
                    </div>
                    <StructureElementHeroSliderBanderoleCountdown
                        v-if="slide.countdownEnd"
                        :date="slide.countdownEnd"
                        :type="slide.countdownType ?? undefined"
                        :bg-color="slide.banderoleBgColor ?? undefined"
                        class="justify-center items-center gap-2 capitalize"
                        v-bind="$attrs"
                    />
                </div>

                <div v-if="isDesktop">
                    {{ hasContent(slide.banderoleText2) ? slide.banderoleText2 : slide.banderoleText1 }}
                </div>
            </div>

            <div
                v-else
                class="flex justify-center md:justify-between items-center text-lg w-full"
                :class="slide.countdownType === 'A' ? 'h-12' : 'h-11'"
            >
                <div v-if="!isMobile">{{ slide.banderoleText1 }}</div>
                <div v-if="hasContent(slide.banderoleText2) && !isMobile && !isTablet">{{ slide.banderoleText2 }}</div>
                <div v-else-if="!hasContent(slide.banderoleText2) && isDesktop">{{ slide.banderoleText1 }}</div>

                <div
                    class="flex justify-center items-center"
                    :class="slide.countdownType === 'A' ? 'h-12' : 'h-11'"
                >
                    <div v-if="hasContent(slide.countdownText)" class="pr-6 text-sm md:text-lg">
                        {{ slide.countdownText }}
                    </div>
                    <StructureElementHeroSliderBanderoleCountdown
                        v-if="slide.countdownEnd"
                        :date="slide.countdownEnd"
                        :type="slide.countdownType ?? undefined"
                        :bg-color="slide.banderoleBgColor ?? undefined"
                        class="justify-center items-center gap-2 capitalize"
                        v-bind="$attrs"
                    />
                </div>

                <div v-if="!isMobile">{{ slide.banderoleText1 }}</div>
                <div v-if="hasContent(slide.banderoleText2) && !isMobile && !isTablet">{{ slide.banderoleText2 }}</div>
                <div v-else-if="!hasContent(slide.banderoleText2) && isDesktop">{{ slide.banderoleText1 }}</div>
            </div>
        </div>
    </div>
    <div
        v-else
        class="banderole absolute left-0 right-0 top-0 px-4 md:px-12 flex py-3 text-lg text-base-100 font-bold"
        :class="{
            'bg-base-content': !slide.banderoleBgColor,
            'uppercase': slide.banderoleTextAlLCaps,
            'justify-between': totalTextCounts > 1,
            'justify-center': totalTextCounts === 1
        }"
        :style="slide.banderoleBgColor ? {'background-color': slide.banderoleBgColor} : undefined"
    >
        <div v-for="i in totalTextCounts" :key="i">
            <template v-if="i % 2 !== 0">
                {{ slide.banderoleText1 }}
            </template>
            <template v-if="i % 2 === 0">
                {{ slide.banderoleText2 ? slide.banderoleText2 : slide.banderoleText1 }}
            </template>
        </div>
    </div>
</template>